import { translateKey } from '@helpers/resolveTranslationsKey';
import { globalDesignerStore } from '@stores/module-stores/designer.store';
import { globalFormsStore } from '@stores/module-stores/forms';
import { globalTagsStore } from '@stores/module-stores/tags';
import { BaseForm } from '@vincerosoft/assessfluence-types/build/src/types/forms/form.interface';
import { getCurrentCreator } from '@widgets/surveyjs/creatorAssessor';
import {
  Model,
  Question,
  QuestionDropdownModel,
  QuestionTagboxModel,
  Serializer
} from 'survey-core';
import { SurveyQuestionEditorDefinition } from 'survey-creator-core';

let propsRegistered = false;

type SerializerPropertyConfig = {
  name: string;
  [key: string]: unknown;
};

function getDesignerItemState() {
  const designerState = globalDesignerStore?.getState();
  if (!designerState) return null;

  const activeItemId = designerState.activeItemId;
  if (!activeItemId) return null;

  return designerState.byItemId[activeItemId] ?? null;
}

function syncProperties(className: string, properties: SerializerPropertyConfig[]) {
  properties.forEach((property) => {
    Serializer.addProperty(className, {
      ...property
    });
  });
}

function mapExpressionQuestionsToChoices(
  obj: Question,
  choicesCb: (arg0: { value: string; text: string }[]) => void
) {
  const creator = getCurrentCreator(); // SurveyCreator | null
  const survey = creator?.survey;
  if (!survey) {
    return;
  }

  const allQuestions = survey.getAllQuestions();

  if (!allQuestions?.length) return choicesCb ? choicesCb([]) : null;
  const validQuestions = allQuestions.filter(
    (q) => q.name !== obj?.name && q.getType() === 'expression'
  );

  const choices = validQuestions?.length
    ? validQuestions.map((question) => {
        return {
          value: question.name,
          text: (question as unknown as { title: string }).title || question.name
        };
      })
    : [];

  if (choicesCb) choicesCb(choices);
}

export function registerCustomSurveyProps() {
  if (propsRegistered) return;

  const choicesFromQuestionTagBox = Serializer.getProperty('tagbox', 'choicesFromQuestion');
  const choicesFromQuestionDropdown = Serializer.getProperty('dropdown', 'choicesFromQuestion');
  [choicesFromQuestionTagBox, choicesFromQuestionDropdown].forEach((prop) => {
    prop.visibleIf = (obj) => obj?.dynamicChoices === 'choicesFromQuestion';
    prop.dependsOn = ['dynamicChoices'];
    prop.visibleIndex = 2;
  });

  ['radiogroup', 'checkbox', 'dropdown', 'tagbox', 'rating', 'imagepicker'].forEach((className) =>
    syncProperties(className, [
      {
        name: 'maxScore',
        category: 'choices',
        displayName: 'Max Score',
        type: 'number'
      },
      {
        name: 'dynamicChoices',
        displayName: 'Dynamic Choices',
        category: 'choices',
        visibleIf: getDesignerItemState()?.intent && getDesignerItemState()!.intent !== 'report',
        choices: (
          _obj: Question,
          choicesCb: (choices: Array<{ value: string; text: string }>) => void
        ) => {
          const itemId = getDesignerItemState()?.itemId;
          const form = (globalFormsStore.getState().forms || []).find((f) => f.id === itemId);
          return choicesCb
            ? choicesCb([
                { value: 'choicesFromQuestion', text: 'From Question' },
                ...((form as BaseForm)?.options?.linked_table_id
                  ? [
                      {
                        value: 'choicesFromTableItem',
                        text: 'From Table Item'
                      }
                    ]
                  : []),
                ...(getDesignerItemState()?.intent === 'table'
                  ? [
                      {
                        value: 'choicesFromEntities',
                        text: 'From entities'
                      },
                      {
                        value: 'choicesFromTff',
                        text: 'From another TFF'
                      }
                    ]
                  : [])
              ])
            : null;
        },
        visibleIndex: 1
      },
      {
        name: 'choicesFromTffFormId',
        displayName: 'Table Factory Form',
        dependsOn: ['dynamicChoices'],
        category: 'choices',
        visibleIf: (obj: { dynamicChoices: string }) => {
          return obj?.dynamicChoices === 'choicesFromTff';
        },
        visibleIndex: 3,
        choices: (
          _obj: QuestionTagboxModel | QuestionDropdownModel,
          choicesCb: (arg0: { value: string; text: string }[]) => void
        ) => {
          const linkedTffFormModel = getDesignerItemState()?.linkedTffFormModel;

          if (!linkedTffFormModel?.json) {
            if (choicesCb) choicesCb([]);
            return;
          }

          /*   globalFormsStore.subscribe(
            (state) => state.forms,
            (forms) => {
              if (!forms || !forms.length) {
                if (choicesCb) choicesCb([]);
                return;
              }

              const selectedTableForm = source() as TableForm;
              // backward compatibility

              if (!selectedTableForm?.data_context) {
                if (choicesCb) choicesCb([]);
                return;
              }

              const validForms = forms.filter(
                (opt) =>
                  opt.intent === 'table' &&
                  opt.data_context === selectedTableForm.data_context &&
                  JSON.stringify(opt.name) !== JSON.stringify((obj?.survey as SurveyModel)?.title)
              );

              const choices = validForms?.length
                ? validForms.map((form) => ({
                    value: form.id,
                    text: translateKey(form.internal_name || form.name)
                  }))
                : [];
              if (choicesCb) choicesCb(choices);
            },
            {
              fireImmediately: true
            }
          );*/
        }
      },
      {
        name: 'choicesFromEntitiesFilter',
        displayName: 'Filter entities by Tag',
        dependsOn: ['dynamicChoices'],
        category: 'choices',
        visibleIf: (obj: { dynamicChoices: string }) => {
          return obj?.dynamicChoices === 'choicesFromEntities';
        },
        choices: async (
          _obj: QuestionTagboxModel | QuestionDropdownModel,
          choicesCb: (arg0: { value: string; text: string }[]) => void
        ) => {
          const tags = globalTagsStore.getState().tags.entities || [];

          if (!tags.length) {
            if (choicesCb) choicesCb([]);
            return;
          }

          const choices = tags.map((tag) => ({
            value: tag.id,
            text: tag.name
          }));
          if (choicesCb) choicesCb(choices);
        },
        visibleIndex: 3
      },
      {
        name: 'choicesFromEntitiesVariableName',
        displayName: 'Entity variable name',
        dependsOn: ['dynamicChoices'],
        category: 'choices',
        visibleIf: (obj: { dynamicChoices: string }) =>
          obj?.dynamicChoices === 'choicesFromEntities',
        visibleIndex: 3
      },
      {
        name: 'variablePath',
        displayName: 'Linked question',
        dependsOn: ['dynamicChoices'],
        category: 'choices',
        visibleIf: (obj: { dynamicChoices: string }) =>
          obj?.dynamicChoices === 'choicesFromTableItem',
        choices: (
          _obj: QuestionTagboxModel | QuestionDropdownModel,
          choicesCb: (arg0: { value: string; text: string }[]) => void
        ) => {
          const linkedTffFormModel = getDesignerItemState()?.linkedTffFormModel;

          if (!linkedTffFormModel?.json) {
            if (choicesCb) choicesCb([]);
            return;
          }
          const survey = new Model(linkedTffFormModel?.json || {});
          const questions = survey.getAllQuestions();
          if (!questions || !questions.length) {
            if (choicesCb) choicesCb([]);
            return;
          }

          const validQuestions = questions.filter((q) => q.getType() === 'tagbox');

          const choices = validQuestions?.length
            ? validQuestions.map((question) => ({
                value: question.name,
                text: translateKey(question.title)
              }))
            : [];

          if (choicesCb) choicesCb(choices);
        },
        visibleIndex: 4
      }
    ])
  );

  syncProperties('survey', [
    {
      name: 'class',
      displayName: 'Class',
      category: 'general'
    },
    {
      name: 'id',
      displayName: 'Id',
      category: 'general'
    },
    {
      name: 'showAutosaveNotifications',
      displayName: 'Show Autosave Notifications',
      type: 'boolean',
      category: 'general'
    },
    {
      name: 'data_context',
      visible: false,
      enableIf: () => getDesignerItemState()!.intent === 'table'
    },
    {
      name: 'pageEditMode',
      category: 'general',
      visibleIf: () => getDesignerItemState()!.intent !== 'in-app',
      choices: [
        { value: 'standard', text: 'Standard' },
        { value: 'bypage', text: 'By page' }
        // { value: 'single', text: 'Single' }
      ],
      visibleIndex: 3
    },
    {
      name: 'collapsePages',
      category: 'general',
      visibleIndex: 4,
      type: 'boolean'
    },
    {
      name: 'collapsePanels',
      category: 'general',
      visibleIndex: 5,
      type: 'boolean'
    },
    {
      name: 'collapseQuestions',
      category: 'general',
      visibleIndex: 6,
      type: 'boolean'
    },
    {
      name: 'collapseOnDrag',
      displayName: 'Collapse on drag',
      category: 'general',
      visibleIndex: 7,
      type: 'boolean'
    },
    {
      name: 'summarizeResponseWithAI',
      displayName: 'Summarize response with AI',
      type: 'boolean'
    },
    {
      name: 'aiResponseSummaryPrompt',
      dependsOn: ['summarizeResponseWithAI'],
      displayName: 'AI Response Summary Prompt',
      type: 'comment',
      visibleIf: (obj: { summarizeResponseWithAI: boolean }) => !!obj?.summarizeResponseWithAI
    },
    {
      name: 'aiSummaryTargetQuestion',
      dependsOn: ['summarizeResponseWithAI'],
      displayName: 'Target Question (where the result should go)',
      visibleIf: (obj: { summarizeResponseWithAI: boolean }) => obj?.summarizeResponseWithAI,
      choices: mapExpressionQuestionsToChoices
    }
  ]);

  syncProperties('file', [
    {
      name: 'analyzeImageWithAI',
      displayName: 'Analyze Image with AI',
      type: 'boolean'
    },
    {
      name: 'aiImageAnalysisPrompt',
      dependsOn: ['analyzeImageWithAI'],
      displayName: 'AI Image Analysis Prompt',
      type: 'comment',
      visibleIf: (obj: { analyzeImageWithAI: boolean }) => !!obj?.analyzeImageWithAI
    },
    {
      name: 'aiAnalysisTargetQuestion',
      dependsOn: ['analyzeImageWithAI'],
      displayName: 'Target Question (where the result should go)',
      visibleIf: (obj: { analyzeImageWithAI: boolean }) => obj?.analyzeImageWithAI,
      choices: mapExpressionQuestionsToChoices
    }
  ]);

  syncProperties('question', [
    {
      name: 'includeInCampaignPrefill',
      displayName: 'Include in campaign prefill',
      type: 'boolean'
    },
    {
      name: 'isPrefillReadOnly',
      dependsOn: ['includeInCampaignPrefill'],
      visibleIf: (obj: { includeInCampaignPrefill: boolean }) => obj?.includeInCampaignPrefill,
      displayName: 'Make answer read-only after it’s prefilled',
      type: 'boolean'
    },
    {
      name: 'addToCharts',
      displayName: 'Add to charts dashboard',
      type: 'boolean'
    },
    {
      name: 'triggerCustomExpression',
      displayName: 'Trigger custom expression on change',
      type: 'boolean'
    },
    {
      name: 'hideFromCsvExport',
      displayName: 'Hide from CSV Export',
      type: 'boolean'
    },
    {
      name: 'hideFromDownload',
      displayName: 'Hide from response download',
      type: 'boolean'
    },
    {
      name: 'customExpression',
      dependsOn: ['triggerCustomExpression'],
      displayName: 'Custom Expressions',
      visibleIf: (obj: { triggerCustomExpression: boolean }) => obj?.triggerCustomExpression,
      choices: (_obj: Question, choicesCb: (arg0: { value: string; text: string }[]) => void) => {
        const linkedExpressions = getDesignerItemState()?.linkedExpressions ?? [];

        if (!linkedExpressions.length) {
          if (choicesCb) choicesCb([]);
          return;
        }
        const choices = linkedExpressions.map((expr) => ({
          value: expr.placeholder,
          text: expr.name
        }));
        if (choicesCb) choicesCb(choices);
      }
    },
    {
      name: 'expressionParams',
      dependsOn: ['triggerCustomExpression'],
      displayName: 'Additional question values expressions params',
      visibleIf: (obj: { triggerCustomExpression: boolean }) => obj?.triggerCustomExpression,
      type: 'multiplevalues',
      choices: (obj: Question, choicesCb: (arg0: { value: string; text: string }[]) => void) => {
        if (obj?.survey == null) {
          if (choicesCb) choicesCb([]);
          return;
        }
        const questionNames =
          (
            (obj as Question).survey as {
              questionHashes?: { names: Record<string, Question> };
            }
          )?.questionHashes?.names || {};

        if (!Object.keys(questionNames || {})?.length) {
          if (choicesCb) choicesCb([]);
          return;
        }

        const choices = Object.keys(questionNames || {}).map((name) => {
          const question = questionNames[name];

          const textValue = Array.isArray(question) && question[0].title;
          return {
            value: name,
            text: textValue || name
          };
        });

        if (choicesCb) choicesCb(choices);
      }
    },
    {
      name: 'customExpressionParams',
      dependsOn: ['triggerCustomExpression'],
      displayName: 'Custom expressions params',
      visibleIf: (obj: { triggerCustomExpression: boolean }) => obj?.triggerCustomExpression
    },
    {
      name: 'targetQuestion',
      dependsOn: ['triggerCustomExpression'],
      displayName: 'Target Question (where the result should go)',
      visibleIf: (obj: { triggerCustomExpression: boolean }) => obj?.triggerCustomExpression,
      choices: mapExpressionQuestionsToChoices
    }
  ]);

  syncProperties('expression', [
    {
      name: 'triggerCustomExpressionOnLoad',
      category: 'general',
      displayName: 'Trigger custom expression on survey load',
      type: 'boolean',
      visibleIf: () => {
        return getDesignerItemState()?.itemType === 'report';
      }
    },
    {
      name: 'customExpressionOnLoad',
      category: 'general',
      displayName: 'Custom Expressions',
      visibleIf: (obj: { triggerCustomExpressionOnLoad: boolean }) =>
        obj?.triggerCustomExpressionOnLoad,
      dependsOn: ['triggerCustomExpressionOnLoad'],
      choices: (_obj: Question, choicesCb: (arg0: { value: string; text: string }[]) => void) => {
        const linkedExpressions = getDesignerItemState()?.linkedExpressions;

        if (!linkedExpressions?.length) {
          if (choicesCb) choicesCb([]);
          return;
        }
        const choices = linkedExpressions.map((expr) => ({
          value: expr?.placeholder,
          text: expr?.name
        }));
        if (choicesCb) choicesCb(choices);
      }
    },
    {
      name: 'customExpressionOnLoadParams',
      category: 'general',
      dependsOn: ['triggerCustomExpressionOnLoad'],
      displayName: 'Custom expressions params',
      visibleIf: (obj: { triggerCustomExpressionOnLoad: boolean }) =>
        obj?.triggerCustomExpressionOnLoad
    }
  ]);

  ['multipletextitem', 'boolean', 'text', 'question'].forEach((className) =>
    syncProperties(className, [
      {
        name: 'valuePath',
        displayName: 'Value Path'
      }
    ])
  );
  syncProperties('itemvalue', [
    {
      name: 'score',
      displayName: 'Score',
      type: 'number',
      visibleIf: (obj: { ownerPropertyName?: string }) => obj?.ownerPropertyName !== 'rows'
    }
  ]);

  if (SurveyQuestionEditorDefinition) {
    const definition = SurveyQuestionEditorDefinition.definition;
    if (definition['matrixdropdowncolumn']?.properties) {
      definition['matrixdropdowncolumn'].properties.push('score');
    } else {
      definition['matrixdropdowncolumn'] = {
        ...definition['matrixdropdowncolumn'],
        properties: ['score']
      };
    }
  }

  propsRegistered = true;
}
